import React, { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import type moment from 'moment'

import TextButton from 'LuxKit/Button/TextButton'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import {
  SHORT_DAY_NAME_DAY_MONTH,
  SHORT_DAY_NAME_DAY_MONTH_YEAR,
} from 'constants/dateFormats'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import DateRangePicker from 'tripPlanner/Tripkit/Common/DateRangePicker'

interface Props {
  triggerRef: React.RefObject<HTMLElement | null>
  isOpen: boolean
  startDate?: moment.Moment
  endDate?: moment.Moment
  onDatesChange: (dates: {
    startDate: moment.Moment
    endDate?: moment.Moment | null
  }) => void
  onClear?: () => void
  onClose?: () => void
  startLabel?: string
  endLabel?: string
  rangeType: 'days' | 'nights'
  initDate?: moment.Moment
  portaled?: boolean
  placement?: 'top' | 'bottom'
}

function DateRangeDropdown({
  triggerRef,
  isOpen,
  startDate: startDateProp,
  endDate: endDateProp,
  onDatesChange,
  onClear,
  onClose,
  startLabel,
  endLabel,
  rangeType,
  initDate,
  portaled,
  placement = 'bottom',
}: Props) {
  const intl = useIntl()

  const [dates, setDates] = useState<{ startDate?: moment.Moment, endDate?: moment.Moment }>({
    startDate: startDateProp,
    endDate: endDateProp,
  })
  const { startDate, endDate } = dates

  const hasDates = !!(startDate && endDate)

  const dateRangeLabel = (startDate && endDate) ? (
    rangeType === 'days'
      ? intl.formatMessage(
          {
            id: 'VEFCAPXqDC',
            defaultMessage: '({count, plural, one {# day} other {# days}})',
            description: 'Number of days in selected trip date range',
          },
          { count: endDate.diff(startDate, 'days') + 1 },
        )
      : intl.formatMessage(
          {
            id: 'ns2aGNSkkO',
            defaultMessage: '({count, plural, one {# night} other {# nights}})',
            description: 'Number of nights in selected trip date range',
          },
          { count: endDate.diff(startDate, 'days') },
        )
  ) : undefined

  useEffect(() => {
    setDates({ startDate: startDateProp, endDate: endDateProp })
  }, [startDateProp, endDateProp])

  const apply = useCallback(() => {
    if (dates.startDate) {
      onDatesChange(dates as { startDate: moment.Moment, endDate?: moment.Moment })
    }
  }, [dates, onDatesChange])

  const isDesktop = useScreenSizeUp('desktop')

  return (
    <DropdownSheet
      triggerRef={triggerRef}
      anchorRef={triggerRef}
      size="L"
      open={isOpen}
      placement={placement}
      onClose={onClose}
      crossAxisShift
      portaled={portaled}
    >
      <DateRangePicker
        onDatesChange={setDates}
        startDate={startDate}
        endDate={endDate}
        startLabel={startLabel}
        endLabel={endLabel}
        initDate={initDate}
        type={isDesktop ? 'dual' : 'single'}
        allowSameDay={false}
        showTripDates
      />
      {!!onClear && (
        <Group direction="horizontal" horizontalAlign="end" gap={16}>
          {hasDates && (
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <Group direction="horizontal" gap={4} verticalAlign="center">
                <BodyText variant="medium" weight="bold">
                  {startDate.format(SHORT_DAY_NAME_DAY_MONTH)}
                </BodyText>

                <LineArrowRightIcon size="S" />

                <BodyText variant="medium" weight="bold">
                  {endDate.format(SHORT_DAY_NAME_DAY_MONTH_YEAR)}
                </BodyText>
              </Group>

              <BodyText variant="medium">
                {dateRangeLabel}
              </BodyText>
            </Group>
          )}
          <TextButton kind="tertiary" onClick={onClear} horizontalOutdent="end">
            {intl.formatMessage({
              id: 'cStfuhd8mO',
              defaultMessage: 'Clear dates',
              description: 'Button to clear selected date range in trip planner',
            })}
          </TextButton>
          <TextButton kind="primary" onClick={apply} disabled={!startDate}>
            {intl.formatMessage({
              id: 'AOdjW+PWSg',
              defaultMessage: 'Apply',
              description: 'Button to apply selected date range in trip planner',
            })}
          </TextButton>
        </Group>
      )}
    </DropdownSheet>
  )
}

export default DateRangeDropdown
